import { isLadderPermission, ladderPermissions, type Access } from './access.ts'

/**
 * RBAC vocabulary — shared verbatim by server and clients (01-ARCHITECTURE.md §5).
 *
 * The server *enforces* with `can()`; clients only use it to hide affordances.
 * Role definitions are code, not rows: presets below cover every church until
 * custom roles arrive (Phase 10), and keeping them here means a permission
 * check can never disagree between API, web and mobile.
 *
 * This package has zero runtime dependencies by design.
 */

export const MODULES = [
  'org', // settings, campuses, staff & roles, invitations
  'people',
  'groups',
  'attendance',
  'events',
  'checkin',
  'giving',
  'services',
  'messages',
  'media',
  'reports',
] as const

export type Module = (typeof MODULES)[number]

export type Action = 'read' | 'write' | 'admin'

/**
 * Grants that do not fit `module:action` because they narrow a field or carve
 * one action out of a module.
 *
 * - `people:minors:read` (01-ARCHITECTURE.md §5): a child's own phone, email
 *   and address are visible only to roles that hold it, not to everyone who
 *   can read people.
 * - `checkin:checkout`: handing a child over. Every role that checks children
 *   in holds it — except a station device, which can take a child in but is
 *   never the way one leaves.
 * - `people:contact:read` (13-ACCESS): phones, emails and addresses of the
 *   people seen from ForServices — rosters, plans, scheduling. Without it a
 *   ForServices user sees basic details only, unless they schedule the team
 *   the person serves on (`canSeeContactDetails`).
 */
export const FIELD_PERMISSIONS = [
  'people:minors:read',
  'checkin:checkout',
  'people:contact:read',
] as const
export type FieldPermission = (typeof FIELD_PERMISSIONS)[number]

export type Permission = `${Module}:${Action}` | FieldPermission

/**
 * What stays on a role once ForChurch is on the ladders (step F) — giving
 * and the field permissions — in words, for the Roles screen.
 */
export const ROLE_PERMISSION_LABELS: Readonly<Record<string, string>> = {
  'giving:read': 'Giving: see gifts and givers',
  'giving:write': 'Giving: record gifts and batches',
  'giving:admin': 'Giving: funds, refunds and statements',
  'people:minors:read': 'Children’s own contact details',
  'checkin:checkout': 'Check children out',
  'people:contact:read': 'Contact details of the people seen from ForServices',
}

/** Every permission, for roles that genuinely hold them all. */
export const ALL_PERMISSIONS: readonly Permission[] = [
  ...MODULES.flatMap((m) => [`${m}:read` as const, `${m}:write` as const, `${m}:admin` as const]),
  ...FIELD_PERMISSIONS,
]

/**
 * `giving:*` is deliberately excluded from the broad staff/admin presets.
 * Donation amounts are the single most sensitive field in a church database;
 * the permission is granted explicitly (owner and finance only) and every
 * grant is auditable.
 */
const NON_GIVING = ALL_PERMISSIONS.filter((p) => !p.startsWith('giving:'))

export const ROLES = {
  owner: {
    label: 'Owner',
    description: 'Full access, including giving data, billing and role management.',
    permissions: ALL_PERMISSIONS,
  },
  admin: {
    label: 'Admin',
    description: 'Full access except giving data.',
    permissions: NON_GIVING,
  },
  staff: {
    label: 'Staff',
    description: 'Day-to-day read/write across ministry modules; no settings, no giving.',
    // No `people:minors:read` either: "staff" is the broadest preset, and a
    // child's own contact details are exactly what it should not carry.
    permissions: NON_GIVING.filter(
      (p) => !p.endsWith(':admin') && !p.startsWith('org:') && p !== 'people:minors:read',
    ).concat(['org:read']),
  },
  campus_admin: {
    label: 'Campus admin',
    description: 'Admin within assigned campuses; membership rows carry the campus scope.',
    permissions: NON_GIVING,
  },
  finance: {
    label: 'Finance',
    description: 'Giving, batches, statements and financial reports.',
    permissions: ['org:read', 'giving:read', 'giving:write', 'giving:admin', 'reports:read'],
  },
  group_leader: {
    label: 'Group leader',
    description:
      'Their own groups, rosters and attendance. Authority comes from leading the group, ' +
      'not from this role: `groups:write` would mean every group in the church.',
    // Deliberately no `groups:write`. A leader edits the group they lead and
    // manages its roster because the membership row says `role = 'leader'`,
    // which the service checks per group. Granting the module permission here
    // would make them staff for every other group as well — and the roster of
    // a recovery group is not theirs to read.
    permissions: ['groups:read', 'attendance:read', 'attendance:write', 'people:read'],
  },
  service_scheduler: {
    label: 'Service scheduler',
    description: 'Plans, songs and team scheduling.',
    permissions: ['services:read', 'services:write', 'people:read', 'messages:write'],
  },
  checkin_volunteer: {
    label: 'Check-in volunteer',
    description: 'Runs a check-in station on Sunday morning.',
    permissions: ['checkin:read', 'checkin:write', 'checkin:checkout', 'people:read'],
  },
  checkin_station: {
    label: 'Check-in station (device)',
    description:
      'A registered kiosk, not a person: finds families and checks children in. ' +
      'Held only by the service user a station token resolves to; never assigned to staff.',
    // No people:read, no check-out: a stolen iPad is a stolen check-in desk,
    // not a directory and not a way to collect a child.
    permissions: ['checkin:read', 'checkin:write'],
    deviceOnly: true,
  },
  api_client: {
    label: 'API key (service)',
    description:
      'The service user behind a public API key. Holds nothing itself: a request with the key ' +
      'gets exactly the scopes the key was given.',
    permissions: [],
    deviceOnly: true,
  },
  member: {
    label: 'Member',
    description: 'A congregant: directory, own giving, groups and events.',
    // services:read is the band member on Sunday: the running order and the
    // charts on their Music Stand. Neither is sensitive; scheduling others
    // still needs services:write.
    permissions: ['people:read', 'groups:read', 'events:read', 'media:read', 'services:read'],
  },
} as const satisfies Record<
  string,
  {
    label: string
    description: string
    permissions: readonly Permission[]
    /** Held by devices only: hidden from role pickers, refused on invitations. */
    deviceOnly?: boolean
  }
>

export type RoleId = keyof typeof ROLES

export const ROLE_IDS = Object.keys(ROLES) as readonly RoleId[]

/** Roles a person can be given — everything except device roles. */
export const ASSIGNABLE_ROLE_IDS = ROLE_IDS.filter(
  (id) => !('deviceOnly' in ROLES[id] && ROLES[id].deviceOnly),
)

export function isRoleId(value: string): value is RoleId {
  return value in ROLES
}

/**
 * What a membership row grants: the union of its roles' permissions, scoped to
 * campuses (null = org-wide). Built once per session by the tenancy layer and
 * cached client-side by `usePermissions()`.
 */
export interface Grants {
  permissions: readonly Permission[]
  /** null ⇒ org-wide. Non-null ⇒ permissions apply only within these campuses. */
  campusIds: readonly string[] | null
  /** Modules the church's plan does not include (set by `applyEntitlements`). */
  lockedModules?: readonly Module[]
  /** The account is read-only (trial over, payment failed past grace, cancelled). */
  readOnly?: boolean
}

/** Resolves a membership's role ids (better-auth stores them comma-separated). */
export function grantsForRoles(
  roleIds: readonly string[],
  campusIds: readonly string[] | null = null,
  /** Permissions of the membership's custom roles, already looked up. */
  custom: Readonly<Record<string, readonly string[]>> = {},
): Grants {
  const permissions = new Set<Permission>()
  for (const id of roleIds) {
    if (isRoleId(id)) for (const p of ROLES[id].permissions) permissions.add(p)
    for (const p of custom[id] ?? []) if (isPermission(p)) permissions.add(p)
  }
  return { permissions: [...permissions], campusIds }
}

/** Device roles only (a check-in station, an API key's service user): not a person. */
export const devicesOnly = (roleIds: readonly string[]): boolean =>
  roleIds.length > 0 &&
  roleIds.every((id) => isRoleId(id) && 'deviceOnly' in ROLES[id] && ROLES[id].deviceOnly)

/**
 * What a person may do in a church (docs/13-ACCESS.md, step F). ForChurch's
 * module permissions — `people:read`, `groups:write`, `org:admin`, … — come
 * from their levels on the ladders (their own grants, their preset roles'
 * and the church's default level), not from their roles. Giving and the field
 * permissions stay with their roles, custom roles included. A device keeps
 * its role's fixed permissions; it holds nothing on a ladder. The campus
 * limit stays the membership's.
 */
export function grantsForMember(
  roleIds: readonly string[],
  access: Access,
  campusIds: readonly string[] | null = null,
  custom: Readonly<Record<string, readonly string[]>> = {},
): Grants {
  const fromRoles = grantsForRoles(roleIds, campusIds, custom)
  if (devicesOnly(roleIds)) return fromRoles
  return {
    permissions: [
      ...fromRoles.permissions.filter((permission) => !isLadderPermission(permission)),
      ...ladderPermissions(access),
    ],
    campusIds,
  }
}

/**
 * The check, with three questions it can answer:
 *
 * - `can(g, perm)` — "does the actor hold this permission at all?" A
 *   campus-scoped grant passes; row-level filtering to their campuses is the
 *   data layer's job (services filter every query by the grant's campusIds).
 * - `can(g, perm, { campusId })` — "…for this specific campus?" A scoped
 *   grant passes only for its own campuses.
 * - `can(g, perm, { orgWide: true })` — "…for the organization as a whole?"
 *   (org settings, member management). A campus-scoped grant always fails.
 */
export function can(
  grants: Grants,
  permission: Permission,
  scope?: { campusId?: string; orgWide?: boolean },
): boolean {
  if (!grants.permissions.includes(permission)) return false
  if (grants.campusIds === null) return true
  if (scope?.orgWide) return false
  if (scope?.campusId) return grants.campusIds.includes(scope.campusId)
  return true
}

// ---- custom roles ---------------------------------------------------------------------

/** A church's own role is stored in a membership as `custom:<uuid>`. */
export const CUSTOM_ROLE_PREFIX = 'custom:'
export const customRoleId = (id: string) => `${CUSTOM_ROLE_PREFIX}${id}`
export const parseCustomRoleId = (value: string): string | null =>
  value.startsWith(CUSTOM_ROLE_PREFIX) ? value.slice(CUSTOM_ROLE_PREFIX.length) : null

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as readonly string[]).includes(value)
}

// ---- plans and entitlements ----------------------------------------------------------------

/**
 * What a plan can switch on (Phase 12f). The catalog is code — code enforces
 * every entry — while which plan gets which is data: plans are rows, built
 * in the platform console (admin.forhim.io) and read by `entitlementsFor`.
 *
 * Features are ForChurch's modules (`org` is in every plan: a church without
 * its settings has nothing), the ForServices app and the ForStream app.
 * Limits are numbers; `null` means unlimited.
 */
export const APPS = ['church', 'services', 'stream'] as const
export type App = (typeof APPS)[number]

export const FEATURES = [
  {
    key: 'people',
    app: 'church',
    label: 'People',
    description: 'The directory, households, lists',
  },
  {
    key: 'groups',
    app: 'church',
    label: 'Groups',
    description: 'Small groups, meetings, resources',
  },
  { key: 'attendance', app: 'church', label: 'Attendance', description: 'Headcounts and records' },
  { key: 'events', app: 'church', label: 'Events', description: 'Calendar, registrations, rooms' },
  { key: 'checkin', app: 'church', label: 'Check-in', description: "Children's check-in, kiosks" },
  {
    key: 'giving',
    app: 'church',
    label: 'Giving',
    description: 'Online gifts, batches, statements',
  },
  { key: 'messages', app: 'church', label: 'Messages', description: 'Email, texts, workflows' },
  { key: 'media', app: 'church', label: 'Media', description: 'Sermons and media library' },
  { key: 'reports', app: 'church', label: 'Reports', description: 'Insights and saved reports' },
  {
    key: 'services',
    app: 'services',
    label: 'ForServices',
    description: 'Service plans, songs, scheduling, Music Stand',
  },
  {
    key: 'stream',
    app: 'stream',
    label: 'ForStream',
    description: 'Live streaming, the video library, captions',
  },
] as const satisfies readonly {
  key: string
  app: App
  label: string
  description: string
}[]
export type FeatureKey = (typeof FEATURES)[number]['key']
export const FEATURE_KEYS = FEATURES.map((f) => f.key) as readonly FeatureKey[]
export const isFeatureKey = (value: string): value is FeatureKey =>
  (FEATURE_KEYS as readonly string[]).includes(value)

export const LIMITS = [
  { key: 'seats', app: 'church', label: 'Staff seats', unit: 'seats' },
  // Every file a church keeps: ForStream's saved library and the church's uploads
  // (photos, plan and series files, series art, group resources), one allowance.
  { key: 'media.storageGb', app: 'church', label: 'Media storage', unit: 'GB' },
  { key: 'stream.hoursPerMonth', app: 'stream', label: 'Streaming hours a month', unit: 'hours' },
  // AI credits a month (docs/15, "AI credits"): each AI action costs a set number,
  // priced by platform operators; grants add to a month, refunds give back.
  { key: 'ai_credits', app: 'services', label: 'AI credits a month', unit: 'credits' },
] as const satisfies readonly { key: string; app: App; label: string; unit: string }[]
export type LimitKey = (typeof LIMITS)[number]['key']
export const LIMIT_KEYS = LIMITS.map((l) => l.key) as readonly LimitKey[]
export const isLimitKey = (value: string): value is LimitKey =>
  (LIMIT_KEYS as readonly string[]).includes(value)

export type Limits = Record<LimitKey, number | null>
const UNLIMITED: Limits = Object.fromEntries(LIMIT_KEYS.map((k) => [k, null])) as Limits

/** A plan as entitlements need it (the rest of the row is pricing and display). */
export interface PlanDefinition {
  key: string
  name: string
  features: readonly string[]
  limits: Partial<Record<string, number | null>>
}

/** A platform operator's exceptions for one church: switches and limits that win over the plan. */
export interface EntitlementOverride {
  features: Partial<Record<string, boolean>>
  limits: Partial<Record<string, number | null>>
  expiresAt: Date | null
}

/** The module a permission belongs to: `giving:read` → `giving`. */
export const moduleOf = (permission: Permission): Module => permission.split(':')[0] as Module

/**
 * What the subscription allows right now:
 * - `features` / `modules`: what the plan (and any override) switches on;
 * - `limits`: seats, media storage, streaming hours, AI credits (null: unlimited);
 * - `readOnly`: a payment that failed past its grace — the church can read
 *   and export everything, change nothing, except check-in, which never
 *   stops: a child's pickup does not wait on a card payment.
 * A trial that ran out, or a cancelled plan, falls back to the free plan
 * (`reason` says why, for the banner) — or, with no free plan, read-only.
 */
export interface Entitlements {
  plan: string
  planName: string
  features: readonly FeatureKey[]
  modules: readonly Module[]
  limits: Limits
  readOnly: boolean
  /** Why it is limited, for the banner. */
  reason: 'trial_ended' | 'payment_failed' | 'canceled' | null
}

export const PAST_DUE_GRACE_DAYS = 14

/** Organizations with no subscription row predate plans (and test fixtures): everything. */
const EVERYTHING = { key: 'unmanaged', name: 'Everything', features: FEATURE_KEYS, limits: {} }

function shape(
  plan: PlanDefinition,
  override: EntitlementOverride | null,
  now: Date,
  extra: Pick<Entitlements, 'readOnly' | 'reason'>,
): Entitlements {
  const live = override && (!override.expiresAt || override.expiresAt > now) ? override : null
  const switched = new Set<string>(plan.features)
  for (const [key, on] of Object.entries(live?.features ?? {}))
    if (on === true) switched.add(key)
    else if (on === false) switched.delete(key)
  const features = FEATURE_KEYS.filter((k) => switched.has(k))
  const limits = { ...UNLIMITED }
  for (const key of LIMIT_KEYS) {
    const fromOverride = live?.limits[key]
    const value = fromOverride !== undefined ? fromOverride : plan.limits[key]
    limits[key] = typeof value === 'number' && value >= 0 ? value : null
  }
  const modules = MODULES.filter((m) => m === 'org' || switched.has(m))
  return { plan: plan.key, planName: plan.name, features, modules, limits, ...extra }
}

export function entitlementsFor(
  subscription: {
    plan: string
    status: string
    trialEndsAt: Date | null
    pastDueSince: Date | null
  } | null,
  plans: {
    /** The subscription's plan, when it still exists. */
    current: PlanDefinition | null
    /** The free plan: what a lapsed trial or a cancelled plan falls back to. */
    fallback: PlanDefinition | null
  } = { current: null, fallback: null },
  override: EntitlementOverride | null = null,
  now: Date = new Date(),
): Entitlements {
  if (!subscription) return shape(EVERYTHING, override, now, { readOnly: false, reason: null })
  const plan = plans.current ?? plans.fallback ?? EVERYTHING
  const lapsed =
    subscription.status === 'canceled'
      ? 'canceled'
      : subscription.status === 'trialing' &&
          subscription.trialEndsAt &&
          subscription.trialEndsAt <= now
        ? 'trial_ended'
        : null
  if (lapsed) {
    // Back to the free plan; with none, the old plan, read-only.
    if (plans.fallback)
      return shape(plans.fallback, override, now, { readOnly: false, reason: lapsed })
    return shape(plan, override, now, { readOnly: true, reason: lapsed })
  }
  if (
    subscription.status === 'past_due' &&
    subscription.pastDueSince &&
    now.getTime() - subscription.pastDueSince.getTime() > PAST_DUE_GRACE_DAYS * 86_400_000
  )
    return shape(plan, override, now, { readOnly: true, reason: 'payment_failed' })
  return shape(plan, override, now, { readOnly: false, reason: null })
}

/** Whether a feature (a ForChurch module, ForServices, ForStream) is on. */
export const hasFeature = (entitlements: Pick<Entitlements, 'features'>, key: FeatureKey) =>
  entitlements.features.includes(key)

/** Modules that keep working when an account is read-only. */
const NEVER_LOCKED: readonly Module[] = ['org', 'checkin']

/**
 * Whether the plan lets an action on a module through: not a module the
 * plan leaves out and, on a read-only account, a read — or check-in or
 * Organization, which never lock. `applyEntitlements` on a single check, for
 * clients that read levels rather than permissions.
 */
export function planAllows(
  plan: Pick<Grants, 'lockedModules' | 'readOnly'>,
  module: Module,
  action: Action,
): boolean {
  if (plan.lockedModules?.includes(module)) return false
  return !plan.readOnly || NEVER_LOCKED.includes(module) || action === 'read'
}

/**
 * Grants after the subscription has had its say: permissions for modules
 * outside the plan are removed (so `can()` hides them and the server 403s),
 * and a read-only account keeps only reads — and check-in.
 */
export function applyEntitlements(grants: Grants, entitlements: Entitlements): Grants {
  const permissions = grants.permissions.filter((permission) => {
    const module = moduleOf(permission)
    if (!entitlements.modules.includes(module)) return false
    if (!entitlements.readOnly || NEVER_LOCKED.includes(module)) return true
    return permission.endsWith(':read')
  })
  const locked = MODULES.filter((m) => !entitlements.modules.includes(m))
  return { ...grants, permissions, lockedModules: locked, readOnly: entitlements.readOnly }
}

/** A seat is a person with any role beyond member — and not a device. */
export function usesSeat(roles: readonly string[]): boolean {
  return roles.some((role) => {
    if (parseCustomRoleId(role)) return true
    if (!isRoleId(role)) return false
    const def = ROLES[role]
    return role !== 'member' && !('deviceOnly' in def && def.deviceOnly)
  })
}

// ---- access levels (13-ACCESS) ---------------------------------------------------------------

export * from './access.ts'
export * from './templates.ts'
export * from './services-people.ts'
